"use client"

import { useQuery } from "@tanstack/react-query"
import { ChevronLeft, ChevronRight, CreditCard, DollarSign, Shield, Smartphone, Zap } from "lucide-react"
import type React from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import useLocalStorageArray from "../hooks/useLocalStorageArray"
import { api } from "../services/api"
import "./ServicePaymentWizard.css"

interface Category {
  ID: string
  Nombre: string
}
interface Carrier {
  ID: string
  Nombre: string
  CategoriaID: string
  Logotipo: string
  Campos: Array<{
    Nombre: string
    Campo: string
    Obligatorio: "0" | "1"
    Min?: string
    Max?: string
    Formato?: "1" | "2" | "3"
    iniCero?: "0" | "1"
  }>
}
interface Product {
  proID: string
  CarrierID: string
  CategoriaID: string
  Codigo: string
  Monto: string
  Bolsa: string
  Vigencia: string
  Descripcion: string
}
interface Network {
  id: number
  name: string
  logo_url: string
}
interface TokenAsset {
  id: number
  name: string
  symbol: string
  logo_url: string
}

// Helper function to format currency with commas
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export default function ServicePaymentWizard() {
  const navigate = useNavigate()

  // API queries
  const {
    data: productsData,
    isLoading: loadingProducts,
    error: productsError,
  } = useQuery<{ categorias: Category[]; carriers: Carrier[]; productos: Product[] }>({
    queryKey: ["payProducts"],
    queryFn: () => api.get("/pay/products").then((r) => r.data.data),
    staleTime: 1000 * 60 * 5,
  })

  const catsRaw = productsData?.categorias ?? []
  const carriersRaw = productsData?.carriers ?? []
  const productsRaw = productsData?.productos ?? []

  // Fetch networks first
  const {
    data: networks = [],
    isLoading: loadingNetworks,
    error: networksError,
  } = useQuery<Network[]>({
    queryKey: ["networks"],
    queryFn: () => api.get("/networks").then((r) => r.data),
    staleTime: 1000 * 60 * 10,
  })

  // Fetch all payment assets for all networks in parallel
  const {
    data: allPaymentAssets = {},
    isLoading: loadingPaymentAssets,
    error: paymentAssetsError,
  } = useQuery<Record<number, TokenAsset[]>>({
    queryKey: ["allPaymentAssets", networks.map((n) => n.id)],
    queryFn: async () => {
      if (!networks.length) return {}

      // Fetch payment assets for all networks in parallel
      const promises = networks.map((network) =>
        api
          .get(`/networks/${network.id}/paymentAssets`)
          .then((response) => ({ networkId: network.id, assets: response.data }))
          .catch((error) => {
            console.warn(`Failed to fetch assets for network ${network.id}:`, error)
            return { networkId: network.id, assets: [] }
          }),
      )

      const results = await Promise.all(promises)

      // Convert array to object for easy lookup
      return results.reduce(
        (acc, { networkId, assets }) => {
          acc[networkId] = assets
          return acc
        },
        {} as Record<number, TokenAsset[]>,
      )
    },
    enabled: networks.length > 0,
    staleTime: 1000 * 60 * 10,
  })

  // Remove the old selectedNet state and tokens query, replace with:
  const [selectedNet, setSelectedNet] = useState<Network | null>(null)

  // Get tokens for the selected network from our pre-fetched data
  const tokens = selectedNet ? allPaymentAssets[selectedNet.id] || [] : []

  // Normalize data
  const categories = useMemo(() => catsRaw.map((c) => ({ id: c.ID, name: c.Nombre })), [catsRaw])
  const carriers = useMemo(
    () =>
      carriersRaw.map((c) => ({
        id: c.ID,
        name: c.Nombre,
        logo: c.Logotipo,
        catId: c.CategoriaID,
        campos: c.Campos,
      })),
    [carriersRaw],
  )
  const products = useMemo(
    () =>
      productsRaw.map((p) => ({
        id: p.Codigo,
        proID: p.proID,
        carrierId: p.CarrierID,
        catId: p.CategoriaID,
        amount: Number.parseFloat(p.Monto),
        bolsa: p.Bolsa,
        vigencia: p.Vigencia,
        descripcion: p.Descripcion,
      })),
    [productsRaw],
  )

  // Wizard state
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedCat, setSelectedCat] = useState<string>("")
  const [selectedCarrier, setSelectedCarrier] = useState<string>("")
  const [selectedProduct, setSelectedProduct] = useState<string>("")
  const [showFloatingButton, setShowFloatingButton] = useState(false)
  const [showFloatingSubmit, setShowFloatingSubmit] = useState(false)
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false)

  // Form state
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [customAmount, setCustomAmount] = useState<number>(0)
  const [refundAddr, setRefundAddr] = useState<string>("")
  const [selectedTok, setSelectedTok] = useState<TokenAsset | null>(null)

  const [quote, setQuote] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Suggestion buckets
  const [prevPhones, addPrevPhone] = useLocalStorageArray("pm-phones", [])
  const [prevEmails, addPrevEmail] = useLocalStorageArray("pm-emails", [])
  const [prevRefunds, addPrevRefund] = useLocalStorageArray("pm-refunds", [])

  // Helper functions
  const strip = (s: string) =>
    s
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
  const isPhoneField = (label: string) => {
    const t = strip(label)
    return t.includes("celular") || t.includes("telefonico")
  }

  // Pricing logic
  const numericAmount = useMemo(() => {
    if (!selectedProduct) return 0
    return products.find((p) => p.id === selectedProduct)?.amount ?? 0
  }, [selectedProduct, products])
  const baseAmount = numericAmount > 0 ? numericAmount : customAmount
  const totalAmount = baseAmount + 5

  // Get bolsa name from selected product
  const bolsaName = useMemo(() => {
    if (!selectedProduct) return ""
    return products.find((p) => p.id === selectedProduct)?.bolsa ?? ""
  }, [selectedProduct, products])

  // Get selected product details
  const selectedProductDetails = useMemo(() => {
    if (!selectedProduct) return null
    return products.find((p) => p.id === selectedProduct)
  }, [selectedProduct, products])

  // Filtered lists
  const visibleCarriers = carriers.filter((c) => c.catId === selectedCat)
  const visibleProducts = products.filter(
    (p) => p.catId === selectedCat && p.carrierId === selectedCarrier && p.amount > 0,
  )
  const onlyCustom =
    products.some((p) => p.catId === selectedCat && p.carrierId === selectedCarrier && p.amount === 0) &&
    visibleProducts.length === 0

  // Show/hide floating button based on scroll and selections
  useEffect(() => {
    if (step !== 1) {
      setShowFloatingButton(false)
      return
    }

    const canContinue = Boolean(selectedCarrier && (onlyCustom || (visibleProducts.length > 0 && selectedProduct)))

    const handleScroll = () => {
      const stepContent = document.querySelector(".step-content")
      if (stepContent && canContinue) {
        const { scrollTop, scrollHeight, clientHeight } = stepContent
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100
        setShowFloatingButton(canContinue && !isNearBottom && window.innerWidth > 1024)
      }
    }

    const stepContent = document.querySelector(".step-content")
    if (stepContent) {
      stepContent.addEventListener("scroll", handleScroll)
      handleScroll() // Check initial state
    }

    return () => {
      if (stepContent) {
        stepContent.removeEventListener("scroll", handleScroll)
      }
    }
  }, [step, selectedCarrier, selectedProduct, onlyCustom, visibleProducts.length])

  // Show/hide floating submit button for step 2
  useEffect(() => {
    if (step !== 2) {
      setShowFloatingSubmit(false)
      return
    }

    const canSubmit = Boolean(selectedNet && selectedTok && baseAmount > 0)

    const handleScroll = () => {
      const stepContent = document.querySelector(".step-content")
      if (stepContent && canSubmit) {
        const { scrollTop, scrollHeight, clientHeight } = stepContent
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100
        setShowFloatingSubmit(canSubmit && !isNearBottom && window.innerWidth > 1024)
      }
    }

    const stepContent = document.querySelector(".step-content")
    if (stepContent) {
      stepContent.addEventListener("scroll", handleScroll)
      handleScroll() // Check initial state
    }

    return () => {
      if (stepContent) {
        stepContent.removeEventListener("scroll", handleScroll)
      }
    }
  }, [step, selectedNet, selectedTok, baseAmount])

  // Fetch quote
  const fetchQuote = useCallback(() => {
    if (step !== 2 || !baseAmount || !selectedNet || !selectedTok) return
    setLoading(true)
    api
      .post("/quote", {
        fromChain: selectedNet.name,
        fromToken: selectedTok.symbol,
        amount: totalAmount,
        direction: "sell",
        channel: "services",
      })
      .then((r) => setQuote(r.data.data))
      .catch((e) => setError(`Quote failed: ${e.message}`))
      .finally(() => setLoading(false))
  }, [step, baseAmount, selectedNet, selectedTok, totalAmount])
  useEffect(fetchQuote, [fetchQuote])

  // Submit handler
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setLoading(true)
    try {
      const payload = {
        ...formData,
        network: selectedNet!.name,
        token: selectedTok!.symbol,
        amount: totalAmount,
        referencia: formData["referencia"],
        package: selectedProduct,
        carrier: selectedCarrierName,
        category: selectedCatName,
        refundAddress: refundAddr,
        bolsa: bolsaName, // Now properly defined
      }
      const resp = await api.post("/pay/service", payload)

      // Persist suggestions
      Object.entries(formData).forEach(([key, val]) => {
        if (isPhoneField(key)) addPrevPhone(val)
        if (key.toLowerCase().includes("email")) addPrevEmail(val)
      })
      refundAddr && addPrevRefund(refundAddr)

      navigate(`/payments/${resp.data.transaction.paymentId}`)
    } catch (err: any) {
      setError(err.response?.data?.message || "Submit failed")
    } finally {
      setLoading(false)
    }
  }

  const selectedCarrierObj = useMemo(() => carriers.find((c) => c.id === selectedCarrier), [carriers, selectedCarrier])

  const selectedProductLabel =
    numericAmount > 0 ? `${selectedCarrierObj?.name ?? ""} ${formatCurrency(numericAmount)}` : "Personalizado"

  const selectedCatName = useMemo(
    () => categories.find((c) => c.id === selectedCat)?.name ?? "",
    [categories, selectedCat],
  )

  const selectedCarrierName = useMemo(() => {
    const c = carriersRaw.find((c) => c.ID === selectedCarrier)
    return c ? c.Nombre : ""
  }, [carriersRaw, selectedCarrier])

  // Form validation
  const isFormValid = useMemo(() => {
    if (!selectedNet || !selectedTok || baseAmount <= 0) return false

    const requiredFields =
      carriersRaw.find((c) => c.ID === selectedCarrier)?.Campos.filter((f) => f.Obligatorio === "1") ?? []

    return requiredFields.every((field) => {
      const value = formData[field.Campo]
      return value && value.trim().length > 0
    })
  }, [selectedNet, selectedTok, baseAmount, carriersRaw, selectedCarrier, formData])

  // Loading states
  if (loadingProducts || loadingNetworks || loadingPaymentAssets) {
    return (
      <div className="wizard-container">
        <div className="wizard-card">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Cargando servicios…</p>
          </div>
        </div>
      </div>
    )
  }

  if (productsError || networksError || paymentAssetsError) {
    return (
      <div className="wizard-container">
        <div className="wizard-card">
          <div className="error-state">
            <p>Error cargando servicios. Por favor intenta de nuevo.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="wizard-container">
      {/* Floating Continue Button */}
      {showFloatingButton && (
        <div className={`floating-continue ${showFloatingButton ? "visible" : ""}`}>
          <button className="btn-primary" onClick={() => setStep(2)}>
            Continuar
            <ChevronRight className="btn-icon" />
          </button>
        </div>
      )}

      {/* Floating Submit Button */}
      {showFloatingSubmit && isFormValid && (
        <div className={`floating-submit ${showFloatingSubmit ? "visible" : ""}`}>
          <button
            className="btn-primary"
            onClick={() => isFormValid && handleSubmit()}
            disabled={loading || !isFormValid}
          >
            {loading ? (
              <>
                <div className="loading-spinner small"></div>
                Procesando...
              </>
            ) : (
              <>
                Pagar {formatCurrency(totalAmount)} MXN
                <ChevronRight className="btn-icon" />
              </>
            )}
          </button>
        </div>
      )}

      <div className="wizard-card">
        {/* Compact Progress indicator */}
        <div className="progress-indicator">
          <div className={`progress-step ${step >= 1 ? "active" : ""}`}>
            <div className="step-number">1</div>
            <span>Seleccionar</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? "active" : ""}`}>
            <div className="step-number">2</div>
            <span>Pagar</span>
          </div>
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="step-content">
            {/* Categories */}
            <div className="section">
              <h3>
                <CreditCard className="section-icon" />
                Categoría
              </h3>
              <div className="categories-grid">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    className={`category-card ${c.id === selectedCat ? "selected" : ""}`}
                    onClick={() => {
                      setSelectedCat(c.id)
                      setSelectedCarrier("")
                      setSelectedProduct("")
                    }}
                  >
                    {c.name.charAt(0).toUpperCase() + c.name.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Compact Carrier + Package Layout */}
            {selectedCat && (
              <div className="compact-selection-container">
                {/* Selected Carrier Summary */}
                {selectedCarrier && (
                  <div className="selected-carrier-summary">
                    <div className="selected-carrier-info">
                      <img
                        src={selectedCarrierObj?.logo || "/placeholder.svg"}
                        alt={selectedCarrierObj?.name}
                        className="selected-carrier-logo"
                      />
                      <span className="selected-carrier-name">{selectedCarrierObj?.name}</span>
                      <button
                        className="change-carrier-btn"
                        onClick={() => {
                          setSelectedCarrier("")
                          setSelectedProduct("")
                        }}
                      >
                        Cambiar
                      </button>
                    </div>
                  </div>
                )}

                {/* Carriers Grid - Compact when carrier is selected */}
                <div className={`carriers-section ${selectedCarrier ? "compact" : ""}`}>
                  <h3>
                    <Smartphone className="section-icon" />
                    Compañía
                  </h3>
                  <div className={`carriers-grid ${selectedCarrier ? "minimized" : ""}`}>
                    {visibleCarriers.map((c) => (
                      <div
                        key={c.id}
                        className={`carrier-card ${c.id === selectedCarrier ? "selected" : ""} ${selectedCarrier && c.id !== selectedCarrier ? "dimmed" : ""}`}
                        onClick={() => {
                          setSelectedCarrier(c.id)
                          setSelectedProduct("")
                        }}
                      >
                        <div className="carrier-logo">
                          <img src={c.logo || "/placeholder.svg"} alt={c.name} />
                        </div>
                        <p className="carrier-name">{c.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Products - Overlapping/Adjacent to carriers */}
                {selectedCarrier && !onlyCustom && (
                  <div className="products-section">
                    <h3>
                      <DollarSign className="section-icon" />
                      Paquete
                    </h3>
                    <div className="products-grid">
                      {visibleProducts.map((p) => (
                        <button
                          key={p.id}
                          className={`product-card ${p.id === selectedProduct ? "selected" : ""}`}
                          onClick={() => setSelectedProduct(p.id)}
                        >
                          <div className="product-amount">{formatCurrency(p.amount)} mxn</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Continue button - hidden when floating button is shown */}
            {selectedCarrier && (onlyCustom || (visibleProducts.length > 0 && selectedProduct)) && (
              <div className={`step-actions ${showFloatingButton ? "hidden" : ""}`}>
                <button className="btn-primary" onClick={() => setStep(2)}>
                  Continuar
                  <ChevronRight className="btn-icon" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="step-content">
            <div className="step-header">
              <h3>Confirmar pago</h3>
              <div className="payment-summary-card">
                <div className="summary-header">
                  <div className="service-info">
                    <h4 className="service-title">{selectedProductLabel}</h4>
                    <div className="service-price">{formatCurrency(totalAmount)} MXN</div>
                  </div>
                </div>

                {selectedProductDetails && (selectedProductDetails.vigencia || selectedProductDetails.descripcion) && (
                  <div className="product-details">
                    <button
                      className="details-toggle"
                      onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                      type="button"
                    >
                      <span>Detalles del servicio</span>
                      <ChevronRight className={`toggle-icon ${isDetailsExpanded ? "expanded" : ""}`} />
                    </button>

                    <div className={`details-content ${isDetailsExpanded ? "expanded" : ""}`}>
                      {selectedProductDetails.vigencia && (
                        <div className="detail-item validity">
                          <div className="detail-icon">⏱️</div>
                          <div className="detail-content">
                            <span className="detail-label">Vigencia</span>
                            <span className="detail-value">{selectedProductDetails.vigencia}</span>
                          </div>
                        </div>
                      )}

                      {selectedProductDetails.descripcion && (
                        <div className="detail-item description">
                          <div className="detail-icon">📋</div>
                          <div className="detail-content">
                            <span className="detail-label">Incluye</span>
                            <div className="detail-description">{selectedProductDetails.descripcion}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="summary-footer">
                  <div className="total-row">
                    <span className="total-label">Total a pagar</span>
                    <span className="total-amount">{formatCurrency(totalAmount)} MXN</span>
                  </div>
                </div>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="payment-form">
              {/* Service Details */}
              <div className="form-section">
                {/* <h4>Detalles del servicio</h4> */}
                <div className="form-grid">
                  {carriersRaw
                    .find((c) => c.ID === selectedCarrier)
                    ?.Campos.map((f) => {
                      const name = f.Campo
                      const label = f.Nombre + (f.Obligatorio === "1" ? " *" : "")
                      const type = isPhoneField(f.Nombre) ? "tel" : f.Formato === "3" ? "email" : "text"
                      const list = isPhoneField(f.Nombre)
                        ? "phone-suggestions"
                        : f.Formato === "3"
                          ? "email-suggestions"
                          : undefined
                      return (
                        <div className="form-group" key={name}>
                          <label className="form-label">{label}</label>
                          <input
                            name={name}
                            type={type}
                            list={list}
                            required={f.Obligatorio === "1"}
                            minLength={f.Min ? +f.Min : undefined}
                            maxLength={f.Max ? +f.Max : undefined}
                            value={formData[name] ?? ""}
                            onChange={(e) => setFormData((prev) => ({ ...prev, [name]: e.target.value }))}
                            placeholder={`Ingresa ${f.Nombre}`}
                            className="form-input"
                          />
                        </div>
                      )
                    })}

                  {/* Custom amount */}
                  {numericAmount === 0 && (
                    <div className="form-group">
                      <label className="form-label">Monto a pagar *</label>
                      <input
                        type="number"
                        min="1"
                        step="0.01"
                        value={customAmount || ""}
                        onChange={(e) => setCustomAmount(Number.parseFloat(e.target.value) || 0)}
                        required
                        className="form-input"
                        placeholder="0.00"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method */}
              <div className="form-section">
                {/* <h4>
                  <Wifi className="section-icon" />
                  Método de pago
                </h4> */}

                {/* Network Selection */}
                <div className="section">
                  <h3>Red de pago *</h3>
                  <div className="networks-grid">
                    {networks.map((network) => (
                      <div
                        key={network.id}
                        className={`network-card ${selectedNet?.id === network.id ? "selected" : ""}`}
                        onClick={() => {
                          setSelectedNet(network)
                          setSelectedTok(null) // Reset token when network changes
                        }}
                      >
                        <div className="network-logo">
                          <img
                            src={network.logo_url || "/placeholder.svg"}
                            alt={network.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.src = "/placeholder.svg"
                            }}
                          />
                        </div>
                        <p className="network-name">{network.name}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Token Selection */}
                {selectedNet && (
                  <div className="section">
                    <h3>Token de pago *</h3>
                    {loadingPaymentAssets ? (
                      <div className="loading-tokens">
                        <div className="loading-spinner small"></div>
                        <span>Cargando tokens...</span>
                      </div>
                    ) : tokens.length > 0 ? (
                      <div className="tokens-grid">
                        {tokens.map((token) => (
                          <div
                            key={token.id}
                            className={`token-card ${selectedTok?.id === token.id ? "selected" : ""}`}
                            onClick={() => setSelectedTok(token)}
                          >
                            <div className="token-logo">
                              <img
                                src={token.logo_url || "/placeholder.svg"}
                                alt={token.symbol}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement
                                  target.src = "/placeholder.svg"
                                }}
                              />
                            </div>
                            <div className="token-symbol">{token.symbol}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="loading-tokens">
                        <span>No hay tokens disponibles para esta red</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Refund Address */}
                <div className="form-group">
                  <label className="form-label">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Dirección de reembolso (opcional)
                  </label>
                  <input
                    type="text"
                    list="refund-suggestions"
                    value={refundAddr}
                    onChange={(e) => setRefundAddr(e.target.value)}
                    className="form-input"
                    placeholder="Dirección para reembolsos automáticos"
                  />
                  <datalist id="refund-suggestions">
                    {prevRefunds.map((v, i) => (
                      <option key={i} value={v} />
                    ))}
                  </datalist>
                </div>
              </div>

              {/* Quote Display */}
              {selectedNet && selectedTok && (
                <div className="quote-section">
                  <div className="quote-card">
                    <div className="quote-header">
                      <Zap className="w-5 h-5" />
                      <h4>Cotización en tiempo real</h4>
                    </div>
                    <div className="quote-content">
                      {loading ? (
                        <div className="quote-loading">
                          <div className="loading-spinner small"></div>
                          <span>Calculando mejor precio...</span>
                        </div>
                      ) : quote ? (
                        <div className="quote-details">
                          <div className="quote-amount">
                            {quote.quoteAmount} {selectedTok?.symbol}
                          </div>
                          <div className="quote-rate">@ {quote.quoteRate} MXN</div>
                          <div className="quote-network">en {selectedNet?.name}</div>
                        </div>
                      ) : (
                        <div className="quote-placeholder">—</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Form Actions - hidden when floating submit is shown */}
              <div className={`form-actions ${showFloatingSubmit ? "hidden" : ""}`}>
                <button type="button" className="btn-secondary" onClick={() => setStep(1)}>
                  <ChevronLeft className="btn-icon" />
                  Volver
                </button>
                <button type="submit" disabled={loading || !isFormValid} className="btn-primary">
                  {loading ? (
                    <>
                      <div className="loading-spinner small"></div>
                      Procesando...
                    </>
                  ) : (
                    `Pagar ${formatCurrency(totalAmount)} MXN`
                  )}
                </button>
              </div>

              {/* Hidden datalists */}
              <datalist id="phone-suggestions">
                {prevPhones.map((v, i) => (
                  <option key={i} value={v} />
                ))}
              </datalist>
              <datalist id="email-suggestions">
                {prevEmails.map((v, i) => (
                  <option key={i} value={v} />
                ))}
              </datalist>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
